import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  MapPin,
  LogOut,
  CheckCircle2,
  Navigation,
  BookUser,
  Map,
  RefreshCw,
  GripVertical,
  Trash2,
  StickyNote,
  Save,
  X,
  FileText,
  Share2,
  History,
  Phone,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// Refactored components
import { SortableStopItem } from "../components/admin/SortableStopItem";
import { AddPostcodeForm } from "../components/admin/AddPostcodeForm";
import { StatsCards } from "../components/admin/StatsCards";
import { DeleteConfirmModal } from "../components/admin/DeleteConfirmModal";
import { ContactsList } from "../components/admin/ContactsList";
import { LiveMapTab } from "../components/admin/LiveMapTab";
import { HistoryTab } from "../components/admin/HistoryTab";
import { BulkPhoneEditor } from "../components/admin/BulkPhoneEditor";
import { AutoSaveNote } from "../components/admin/AutoSaveNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout }) {
  const [postcodes, setPostcodes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newPostcode, setNewPostcode] = useState("");
  const [newName, setNewName] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [activeTab, setActiveTab] = useState("postcodes");
  const [editingNotes, setEditingNotes] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [loadingMap, setLoadingMap] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPhoneEditor, setShowPhoneEditor] = useState(false);
  const [showPhoneReminder, setShowPhoneReminder] = useState(false);
  const wsRef = useRef(null);

  const headers = {};

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // WebSocket connection for real-time location updates
  // This effect intentionally runs once on mount to establish WebSocket
  useEffect(() => {
    const connectWebSocket = () => {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsProtocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
      const wsHost = apiUrl.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}//${wsHost}/api/ws`;
      
      const ws = new WebSocket(wsUrl);
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'DRIVER_LOCATION') {
            setUserLocation({ ...message.data, updated_at: message.data.updated_at });
          }
          if (message.type === 'STOP_COMPLETED') {
            const { name, postcode, completed_at, completed_by } = message.data;
            const time = new Date(completed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            toast.success(`${name} (${postcode}) completed at ${time}`, { duration: 8000 });
            fetchPostcodes();
          }
        } catch (e) {
          // WebSocket message parse error - non-critical
        }
      };
      
      ws.onclose = () => {
        setTimeout(connectWebSocket, 3000);
      };
      
      wsRef.current = ws;
    };
    
    connectWebSocket();
    return () => wsRef.current?.close();
  }, []);

  // Initialize app - runs once on mount
  useEffect(() => {
    // Wake up the server first, then fetch data
    const initializeApp = async () => {
      try {
        // Health check to wake up server
        await axios.get(`${API}/health`, { timeout: 10000 });
      } catch (err) {
        // Server might be waking up, wait and retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          await axios.get(`${API}/health`, { timeout: 10000 });
        } catch (e) {
          // Server still warming up - will continue anyway
        }
      }
      // Now fetch data
      fetchPostcodes();
      fetchContacts();
      fetchHistory();
      fetchUserLocation();
    };
    initializeApp();
  }, []);

  useEffect(() => {
    if (userLocation?.lat && userLocation?.lng) {
      fetchRouteForAdmin(userLocation);
    }
  }, [userLocation]);

  // 72-hour reminder for contacts without phone numbers
  useEffect(() => {
    if (contacts.length === 0) return;
    const missingPhone = contacts.filter(c => !c.phone?.trim());
    if (missingPhone.length === 0) return;
    const REMINDER_KEY = "landslide_phone_reminder_dismissed";
    const INTERVAL_MS = 72 * 60 * 60 * 1000; // 72 hours
    const lastDismissed = localStorage.getItem(REMINDER_KEY);
    if (lastDismissed && Date.now() - parseInt(lastDismissed, 10) < INTERVAL_MS) return;
    setShowPhoneReminder(true);
  }, [contacts]);


  const fetchUserLocation = async () => {
    try {
      const response = await axios.get(`${API}/user/location`, { headers });
      if (response.data.lat && response.data.lng) {
        setUserLocation(response.data);
      }
    } catch (err) {
      // Location fetch failed - non-critical, map will show default
    }
  };

  const fetchRouteForAdmin = async (location = userLocation) => {
    if (!location?.lat || !location?.lng) return;
    
    setLoadingMap(true);
    try {
      const response = await axios.post(
        `${API}/route/calculate`,
        { lat: location.lat, lng: location.lng },
        { headers }
      );
      if (response.data.route_geometry) {
        setRouteGeometry(response.data.route_geometry);
      }
      setRouteData(response.data);
    } catch (err) {
      // Route fetch failed - non-critical
    } finally {
      setLoadingMap(false);
    }
  };

  const fetchPostcodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/postcodes`, { headers });
      const sorted = response.data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setPostcodes(sorted);
    } catch (err) {
      toast.error("Failed to fetch postcodes");
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, { headers });
      setContacts(response.data);
    } catch (err) {
      // Contacts fetch failed - will show empty list
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/history`, { headers });
      setHistory(response.data);
    } catch (err) {
      // History fetch failed - non-critical
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const pendingPostcodes = postcodes.filter(p => !p.completed);
      const completedPostcodes = postcodes.filter(p => p.completed);
      
      const oldIndex = pendingPostcodes.findIndex(p => p.id === active.id);
      const newIndex = pendingPostcodes.findIndex(p => p.id === over.id);
      
      const reorderedPending = arrayMove(pendingPostcodes, oldIndex, newIndex);
      const newPostcodes = [...reorderedPending, ...completedPostcodes];
      
      setPostcodes(newPostcodes);
      
      try {
        const postcode_ids = reorderedPending.map(p => p.id);
        await axios.put(`${API}/postcodes/reorder`, { postcode_ids }, { headers });
        toast.success("Route order updated!");
        
        if (userLocation?.lat) {
          await fetchRouteForAdmin(userLocation);
        }
      } catch (err) {
        toast.error("Failed to save order");
        fetchPostcodes();
      }
    }
  };

  const handleAddPostcode = async (e) => {
    e.preventDefault();
    if (!newPostcode.trim() || !newName.trim()) {
      toast.error("Please fill in postcode and name");
      return;
    }

    setAdding(true);
    
    // Retry logic for geocoding failures (server might be waking up)
    let response = null;
    let lastError = null;
    
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        response = await axios.post(
          `${API}/postcodes`,
          { postcode: newPostcode.trim(), name: newName.trim() },
          { headers, timeout: 30000 }
        );
        break; // Success, exit retry loop
      } catch (err) {
        lastError = err;
        const errorDetail = err.response?.data?.detail || "";
        
        if (errorDetail.includes("geocode") && attempt < 3) {
          // Geocoding failed, likely server warming up - wait and retry
          toast.info(`Geocoding... (attempt ${attempt}/3)`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        break; // Non-geocoding error or last attempt, exit loop
      }
    }
    
    if (!response) {
      const message = lastError?.response?.data?.detail || "Failed to add postcode";
      toast.error(message);
      setAdding(false);
      return;
    }
      
    if (newNotes.trim()) {
      try {
        await axios.put(
          `${API}/postcodes/${response.data.id}/notes`,
          { notes: newNotes.trim() },
          { headers }
        );
        response.data.notes = newNotes.trim();
      } catch (err) {
        // Notes update failed but postcode was added
      }
    }
    
    setPostcodes([...postcodes, response.data]);

    const existingContact = contacts.find(
      (c) => c.postcode.toUpperCase() === newPostcode.trim().toUpperCase() && 
             c.name.toUpperCase() === newName.trim().toUpperCase()
    );
    if (!existingContact) {
      try {
        const contactResponse = await axios.post(
          `${API}/contacts`,
          { postcode: newPostcode.trim(), name: newName.trim() },
          { headers }
        );
        setContacts([...contacts, contactResponse.data]);
      } catch (err) {
        // Contact save failed - non-critical
      }
    }

    setNewPostcode("");
    setNewName("");
    setNewNotes("");
    toast.success("Postcode added and saved to contacts!");
    setAdding(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/postcodes/${id}`, { headers });
      setPostcodes(postcodes.filter((p) => p.id !== id));
      setDeleteConfirm(null);
      toast.success("Postcode deleted");
    } catch (err) {
      toast.error("Failed to delete postcode");
    }
  };

  const handleToggleComplete = async (id, currentlyCompleted) => {
    try {
      const endpoint = currentlyCompleted ? "uncomplete" : "complete";
      await axios.put(`${API}/postcodes/${id}/${endpoint}`, {}, { headers });
      setPostcodes(postcodes.map(p => 
        p.id === id 
          ? { ...p, completed: !currentlyCompleted, completed_at: currentlyCompleted ? null : new Date().toISOString() } 
          : p
      ));
    } catch (err) {
      toast.error("Failed to update stop");
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers });
      setContacts(contacts.filter((c) => c.id !== id));
      setDeleteConfirm(null);
      toast.success("Contact deleted");
    } catch (err) {
      toast.error("Failed to delete contact");
    }
  };

  const handleSaveAndArchive = async (routeDate) => {
    const completedStops = postcodes.filter(p => p.completed);
    if (completedStops.length === 0) {
      toast.error("No completed stops to archive");
      setDeleteConfirm(null);
      return;
    }

    try {
      // Save to history first
      await axios.post(`${API}/history`, {
        date: routeDate,
        stops: completedStops.map(s => ({
          id: s.id,
          name: s.name,
          postcode: s.postcode,
          notes: s.notes || "",
          completed_at: s.completed_at || null,
        }))
      }, { headers });

      // Then delete from active
      await Promise.all(
        completedStops.map(stop => 
          axios.delete(`${API}/postcodes/${stop.id}`, { headers })
        )
      );
      
      setPostcodes(postcodes.filter(p => !p.completed));
      setDeleteConfirm(null);
      fetchHistory();
      toast.success(`Archived ${completedStops.length} stop${completedStops.length > 1 ? 's' : ''} to history`);
    } catch (err) {
      toast.error("Failed to archive stops");
      fetchPostcodes();
      setDeleteConfirm(null);
    }
  };

  const handleDeleteHistory = async (entryId) => {
    try {
      await axios.delete(`${API}/history/${entryId}`, { headers });
      setHistory(history.filter(h => h.id !== entryId));
      setDeleteConfirm(null);
      toast.success("History entry deleted");
    } catch (err) {
      toast.error("Failed to delete history entry");
    }
  };

  const handleRemoveHistoryStop = async (entryId, stopIndex) => {
    try {
      await axios.delete(`${API}/history/${entryId}/stops/${stopIndex}`, { headers });
      setHistory(prev => prev.map(h => {
        if (h.id !== entryId) return h;
        const stops = h.stops.filter((_, i) => i !== stopIndex);
        return { ...h, stops, stop_count: stops.length, notes_count: stops.filter(s => s.notes).length };
      }));
      setDeleteConfirm(null);
      toast.success("Stop removed from history");
    } catch (err) {
      toast.error("Failed to remove stop");
    }
  };


  const handleUpdateHistoryNote = async (entryId, stopIndex, notes) => {
    try {
      await axios.put(`${API}/history/${entryId}/notes`, { stop_index: stopIndex, notes }, { headers });
      setHistory(prev => prev.map(h => {
        if (h.id !== entryId) return h;
        const updated = { ...h, stops: h.stops.map((s, i) => i === stopIndex ? { ...s, notes } : s) };
        updated.notes_count = updated.stops.filter(s => s.notes).length;
        return updated;
      }));
      toast.success("Note updated");
    } catch (err) {
      toast.error("Failed to update note");
    }
  };

  const handleBulkPhoneSave = async (updates) => {
    try {
      await axios.put(`${API}/contacts/bulk-phones`, { updates }, { headers });
      toast.success("Phone numbers saved!");
      fetchContacts();
    } catch (err) {
      toast.error("Failed to save phone numbers");
    }
  };

  const handleMergeContacts = async (contactIds, name) => {
    try {
      await axios.post(`${API}/contacts/merge`, { contact_ids: contactIds, name }, { headers });
      toast.success("Contacts merged!");
      fetchContacts();
    } catch (err) {
      toast.error("Failed to merge contacts");
    }
  };

  const handleAddFromContact = async (contact) => {
    const existing = postcodes.find(
      (p) => p.postcode.toUpperCase() === contact.postcode.toUpperCase() && 
             p.name.toUpperCase() === contact.name.toUpperCase() &&
             !p.completed
    );
    if (existing) {
      toast.error(`${contact.name} is already in the active route`);
      throw new Error("Already in route");
    }

    try {
      const response = await axios.post(
        `${API}/postcodes`,
        { postcode: contact.postcode, name: contact.name },
        { headers }
      );
      
      // If notes were provided, save them
      if (contact.notes) {
        await axios.put(
          `${API}/postcodes/${response.data.id}/notes`,
          { notes: contact.notes },
          { headers }
        );
        response.data.notes = contact.notes;
      }
      
      setPostcodes([...postcodes, response.data]);
      toast.success(`Added ${contact.name} to route`);
    } catch (err) {
      if (err.message !== "Already in route") {
        toast.error(err.response?.data?.detail || "Failed to add postcode");
      }
      throw err;
    }
  };

  const handleEditNotes = (postcode) => {
    setEditingNotes(editingNotes === postcode.id ? null : postcode.id);
  };

  const handleAutoSaveNotes = async (postcodeId, notes) => {
    await axios.put(
      `${API}/postcodes/${postcodeId}/notes`,
      { notes },
      { headers }
    );
    setPostcodes(prev => prev.map(p => 
      p.id === postcodeId ? { ...p, notes } : p
    ));
  };

  const handleSendCompletedNotes = async () => {
    const completedStops = postcodes.filter(p => p.completed);
    if (completedStops.length === 0) {
      toast.error("No completed stops to share");
      return;
    }

    const today = new Date().toLocaleDateString('en-GB', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    
    let notesContent = `📍 LAND-SLIDE Completed Stops\n📅 ${today}\n━━━━━━━━━━━━━━━━━━━━\n\n`;
    completedStops.forEach((stop, index) => {
      notesContent += `${index + 1}. ${stop.name}\n   📮 ${stop.postcode}\n`;
      if (stop.notes) notesContent += `   📝 ${stop.notes}\n`;
      notesContent += `\n`;
    });
    notesContent += `━━━━━━━━━━━━━━━━━━━━\nTotal: ${completedStops.length} stops completed`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `LAND-SLIDE Completed Stops - ${today}`, text: notesContent });
        toast.success("Notes shared successfully!");
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    
    try {
      await navigator.clipboard.writeText(notesContent);
      toast.success("Notes copied to clipboard!");
    } catch (err) {
      toast.error("Could not share notes");
    }
  };

  const handleShareETALink = async () => {
    const pendingStops = postcodes.filter(p => !p.completed);
    if (pendingStops.length === 0) {
      toast.error("No pending stops to share");
      return;
    }

    const etaPageUrl = `${window.location.origin}/eta`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'LAND-SLIDE Delivery ETAs',
          text: 'Check your delivery ETA:',
          url: etaPageUrl
        });
        toast.success("Link shared!");
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    
    try {
      await navigator.clipboard.writeText(etaPageUrl);
      toast.success("ETA link copied to clipboard!");
    } catch (err) {
      toast.error("Could not copy link");
    }
  };

  const refreshMapData = async () => {
    setLoadingMap(true);
    await fetchUserLocation();
    await fetchPostcodes();
    if (userLocation?.lat) await fetchRouteForAdmin();
    setLoadingMap(false);
    toast.success("Map data refreshed!");
  };

  const formatTime = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  };

  const getEtaForPostcode = (postcodeId) => {
    if (!routeData?.postcodes) return null;
    const pc = routeData.postcodes.find(p => p.id === postcodeId);
    if (!pc) return null;
    if (pc.cumulative_eta || pc.eta_seconds) {
      return {
        cumulative_duration: pc.cumulative_eta || pc.eta_seconds || 0,
        cumulative_distance: pc.cumulative_distance || 0,
      };
    }
    return null;
  };

  // Memoize filtered postcodes to avoid expensive operations in JSX
  const pendingPostcodes = useMemo(() => postcodes.filter(p => !p.completed), [postcodes]);
  const completedPostcodes = useMemo(() => 
    postcodes.filter(p => p.completed).sort((a, b) => {
      const ta = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const tb = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return ta - tb;
    }), [postcodes]);
  const completedCount = completedPostcodes.length;
  const pendingCount = pendingPostcodes.length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 transform rotate-2 italic">LAND-SLIDE</h1>
              <p className="text-xs text-slate-500">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="refresh-btn"
              variant="outline"
              size="sm"
              onClick={async () => {
                setLoading(true);
                await fetchPostcodes();
                await fetchContacts();
                await fetchHistory();
                await fetchUserLocation();
                if (userLocation?.lat) await fetchRouteForAdmin(userLocation);
                setLoading(false);
                toast.success("Data refreshed!");
              }}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <span className="text-sm text-slate-600 hidden sm:block">
              <span className="font-semibold">{user.name}</span>
            </span>
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={onLogout} className="gap-2">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-1">
            <AddPostcodeForm
              newPostcode={newPostcode}
              setNewPostcode={setNewPostcode}
              newName={newName}
              setNewName={setNewName}
              newNotes={newNotes}
              setNewNotes={setNewNotes}
              onSubmit={handleAddPostcode}
              adding={adding}
              contacts={contacts}
              postcodes={postcodes}
              onAddFromContact={handleAddFromContact}
            />
            <StatsCards 
              pendingCount={pendingCount} 
              completedCount={completedCount} 
              contactsCount={contacts.length} 
            />
          </div>

          {/* Right Column - Tabs */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <CardHeader className="pb-4">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="postcodes" data-testid="postcodes-tab" className="gap-1 text-xs sm:text-sm">
                      <Navigation className="w-4 h-4" /><span className="hidden sm:inline">Active</span> ({pendingCount})
                    </TabsTrigger>
                    <TabsTrigger value="contacts" data-testid="contacts-tab" className="gap-1 text-xs sm:text-sm">
                      <BookUser className="w-4 h-4" /><span className="hidden sm:inline">Contacts</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" data-testid="history-tab" className="gap-1 text-xs sm:text-sm">
                      <History className="w-4 h-4" /><span className="hidden sm:inline">History</span>
                    </TabsTrigger>
                    <TabsTrigger value="livemap" data-testid="livemap-tab" className="gap-1 text-xs sm:text-sm">
                      <Map className="w-4 h-4" /><span className="hidden sm:inline">Live Map</span>
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>

                <CardContent>
                  {/* Active Postcodes Tab */}
                  <TabsContent value="postcodes" forceMount className="mt-0" hidden={activeTab !== "postcodes"}>
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
                      </div>
                    ) : postcodes.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No postcodes added yet</p>
                        <p className="text-sm mt-1">Add your first postcode to get started</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[500px] pr-4 custom-scrollbar">
                        {/* Pending stops - draggable */}
                        <div className="mb-4">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                            <GripVertical className="w-3 h-3" />Drag to reorder route
                          </p>
                          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={pendingPostcodes.map(p => p.id)} strategy={verticalListSortingStrategy}>
                              {pendingPostcodes.map((postcode, index) => (
                                <div key={postcode.id} className="mb-3">
                                  <SortableStopItem
                                    postcode={postcode}
                                    index={index}
                                    onDelete={(id) => {
                                      const p = postcodes.find(pc => pc.id === id);
                                      setDeleteConfirm({ type: 'stop', id, name: p?.name || '', postcode: p?.postcode || '' });
                                    }}
                                    onEditNotes={handleEditNotes}
                                    onToggleComplete={handleToggleComplete}
                                    editingNotes={editingNotes}
                                    onAutoSaveNotes={handleAutoSaveNotes}
                                    eta={getEtaForPostcode(postcode.id)}
                                    formatTime={formatTime}
                                  />
                                </div>
                              ))}
                            </SortableContext>
                          </DndContext>
                        </div>

                        {/* Completed stops */}
                        {completedPostcodes.length > 0 && (
                          <div className="mt-6 pt-4 border-t border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-green-600 uppercase tracking-wider">
                                Completed ({completedCount})
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid="archive-all-completed-btn"
                                onClick={() => setDeleteConfirm({ type: 'batch-completed', count: completedCount })}
                                className="text-xs text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 h-7 px-2"
                              >
                                <History className="w-3 h-3 mr-1" />Archive All
                              </Button>
                            </div>
                            {completedPostcodes.map((postcode) => (
                              <div key={postcode.id} className="p-4 rounded-xl border-2 bg-green-50 border-green-200 mb-3">
                                <div className="flex items-start gap-3">
                                  <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm bg-green-500">
                                    <CheckCircle2 className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h3 className="font-semibold text-slate-900 truncate">{postcode.name}</h3>
                                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Done</span>
                                    </div>
                                    <p className="text-sm text-slate-500">{postcode.postcode}</p>
                                    
                                    {editingNotes === postcode.id ? (
                                      <div className="mt-2">
                                        <AutoSaveNote
                                          value={postcode.notes}
                                          onSave={(notes) => handleAutoSaveNotes(postcode.id, notes)}
                                        />
                                      </div>
                                    ) : postcode.notes ? (
                                      <p className="text-xs text-slate-600 bg-amber-50 p-2 rounded border border-amber-100 mt-2 cursor-pointer hover:bg-amber-100" onClick={() => handleEditNotes(postcode)}>
                                        <StickyNote className="w-3 h-3 inline mr-1" />{postcode.notes}
                                      </p>
                                    ) : null}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => handleToggleComplete(postcode.id, true)} className="text-green-500 hover:text-orange-500 hover:bg-orange-50" title="Mark incomplete">
                                      <CheckCircle2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleEditNotes(postcode)} className={`text-slate-400 hover:text-amber-600 ${postcode.notes ? 'text-amber-500' : ''}`}>
                                      <StickyNote className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm({ type: 'stop', id: postcode.id, name: postcode.name, postcode: postcode.postcode })} className="text-slate-400 hover:text-red-500">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    )}
                  </TabsContent>

                  {/* Contact Book Tab */}
                  <TabsContent value="contacts" forceMount className="mt-0" hidden={activeTab !== "contacts"}>
                    <div className="flex justify-end mb-3">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="edit-phones-btn"
                        onClick={() => setShowPhoneEditor(true)}
                        className="gap-1.5 text-xs"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        Edit Contacts
                      </Button>
                    </div>
                    <ContactsList
                      contacts={contacts}
                      postcodes={postcodes}
                      onAddFromContact={(contact) => handleAddFromContact({ postcode: contact.postcode, name: contact.name, notes: '' })}
                      onDeleteContact={(contact) => setDeleteConfirm({ type: 'contact', id: contact.id, name: contact.name, postcode: contact.postcode })}
                      onMergeContacts={handleMergeContacts}
                    />
                  </TabsContent>

                  {/* History Tab */}
                  <TabsContent value="history" forceMount className="mt-0" hidden={activeTab !== "history"}>
                    <HistoryTab
                      history={history}
                      contacts={contacts}
                      onDeleteEntry={(entryId) => {
                        const entry = history.find(h => h.id === entryId);
                        const label = entry ? `${entry.stop_count} stop${entry.stop_count !== 1 ? 's' : ''} — ${entry.date}` : 'this entry';
                        setDeleteConfirm({ type: 'history', id: entryId, label });
                      }}
                      onUpdateNote={handleUpdateHistoryNote}
                      onExport={async (entryId, date) => {
                        try {
                          const response = await axios.post(`${API}/history/${entryId}/export`, {}, { headers, responseType: 'blob' });
                          const text = await response.data.text();
                          if (!text.trim()) {
                            toast.error("No qualifying stops to export (need notes with numbers 50+)");
                            return;
                          }
                          const blob = new Blob([text], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `route-export-${date || 'unknown'}.txt`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success("Route data exported!");
                        } catch (err) {
                          toast.error("Failed to export route data");
                        }
                      }}
                      loading={loadingHistory}
                      onAddStop={async (entryId, stop) => {
                        try {
                          const response = await axios.post(`${API}/history/${entryId}/stops`, stop, { headers });
                          setHistory(prev => prev.map(h => {
                            if (h.id !== entryId) return h;
                            const stops = [...h.stops, response.data];
                            return { ...h, stops, stop_count: stops.length, notes_count: stops.filter(s => s.notes).length };
                          }));
                          toast.success("Stop added to history");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to add stop");
                        }
                      }}
                      onRemoveStop={(entryId, stopIndex, stopName, stopPostcode) => {
                        setDeleteConfirm({ type: 'history-stop', entryId, stopIndex, name: stopName, postcode: stopPostcode });
                      }}
                    />
                  </TabsContent>

                  {/* Live Map Tab */}
                  <TabsContent value="livemap" forceMount className="mt-0" hidden={activeTab !== "livemap"}>
                    <LiveMapTab
                      userLocation={userLocation}
                      postcodes={postcodes}
                      routeGeometry={routeGeometry}
                      loadingMap={loadingMap}
                      onRefresh={refreshMapData}
                    />
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>

            {/* Share ETA Link */}
            {pendingCount > 0 && (
              <div className="mt-6">
                <Button
                  data-testid="share-eta-link-btn"
                  className="w-full gap-2 h-14 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold text-base"
                  onClick={handleShareETALink}
                >
                  <Share2 className="w-5 h-5" />
                  Share ETA Link
                </Button>
              </div>
            )}

            {/* Send Completed Notes */}
            {completedCount > 0 && (
              <div className="mt-4">
                <Button
                  data-testid="send-notes-btn-bottom"
                  className="w-full gap-2 h-14 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-base"
                  onClick={handleSendCompletedNotes}
                >
                  <FileText className="w-5 h-5" />
                  Send Completed Notes ({completedCount})
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Archive / Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          deleteConfirm={deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onDeleteContact={handleDeleteContact}
          onSaveAndArchive={handleSaveAndArchive}
          onDeleteStop={handleDelete}
          onDeleteHistory={handleDeleteHistory}
          onRemoveHistoryStop={handleRemoveHistoryStop}
        />
      )}

      {/* Bulk Phone Number Editor */}
      {showPhoneEditor && (
        <BulkPhoneEditor
          contacts={contacts}
          onSave={handleBulkPhoneSave}
          onClose={() => setShowPhoneEditor(false)}
          onDeleteContact={async (id) => {
            try {
              await axios.delete(`${API}/contacts/${id}`, { headers });
              setContacts(contacts.filter((c) => c.id !== id));
              toast.success("Contact deleted");
            } catch (err) {
              toast.error("Failed to delete contact");
            }
          }}
          onRenameContact={async (id, name) => {
            try {
              await axios.put(`${API}/contacts/${id}/rename`, { name }, { headers });
              setContacts(contacts.map((c) => c.id === id ? { ...c, name } : c));
              toast.success("Contact renamed");
            } catch (err) {
              toast.error("Failed to rename contact");
            }
          }}
          onMergeContacts={handleMergeContacts}
        />
      )}

      {/* Phone Number Reminder Modal */}
      {showPhoneReminder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => {
          localStorage.setItem("landslide_phone_reminder_dismissed", Date.now().toString());
          setShowPhoneReminder(false);
        }}>
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Missing Phone Numbers</h3>
              <p className="text-slate-600 mb-4 text-sm">
                <span className="font-bold text-amber-600">{contacts.filter(c => !c.phone?.trim()).length}</span> contact{contacts.filter(c => !c.phone?.trim()).length !== 1 ? 's' : ''} don't have a phone number yet.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => {
                  localStorage.setItem("landslide_phone_reminder_dismissed", Date.now().toString());
                  setShowPhoneReminder(false);
                }}>
                  Remind Later
                </Button>
                <Button
                  data-testid="phone-reminder-add-btn"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => {
                    localStorage.setItem("landslide_phone_reminder_dismissed", Date.now().toString());
                    setShowPhoneReminder(false);
                    setShowPhoneEditor(true);
                  }}
                >
                  Add Numbers
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
